import React, { Component } from 'react';

import { Button, message } from 'antd';
import Webcam from 'react-webcam';

import styles from './webCam.module.scss';
import { isMobileOrTablet } from '../../utils/platform';

interface WebcamTestState {
  isRecording: boolean;
  isCapturing: boolean;
  videoRecorded: Blob[];
  capturedImage: string | null;
  webcamOpened: boolean;
  finishedRecording: boolean;
  devices: { name: string; deviceId: string }[];
  selectedDeviceId: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IWebcamTestProps {}

export default class WebcamTest extends Component<IWebcamTestProps, WebcamTestState> {
  private webcamRef: React.RefObject<Webcam>;
  private mediaRecorderRef: MediaRecorder | null = null;

  constructor(props: any) {
    super(props);
    this.webcamRef = React.createRef();
    this.capturePhoto = this.capturePhoto.bind(this);
    this.captureVideo = this.captureVideo.bind(this);
    this.onDataAvailable = this.onDataAvailable.bind(this);
    this.stopVideo = this.stopVideo.bind(this);
    this.openCapturePhoto = this.openCapturePhoto.bind(this);
    this.openCaptureVideo = this.openCaptureVideo.bind(this);
    this.closeWebcam = this.closeWebcam.bind(this);
    this.switchCamera = this.switchCamera.bind(this);
    this.state = {
      isCapturing: false,
      isRecording: false,
      videoRecorded: [],
      capturedImage: null,
      webcamOpened: false,
      finishedRecording: false,
      devices: [],
      selectedDeviceId: isMobileOrTablet() ? 'user' : '',
    };
  }

  async componentDidUpdate() {
    const { devices } = this.state;
    if (!isMobileOrTablet() && devices.length === 0) {
      const detectedVideoDevices = (await navigator.mediaDevices.enumerateDevices()).filter(
        ({ kind }) => kind === 'videoinput'
      );
      this.setState({
        devices: detectedVideoDevices.map(({ deviceId, label }) => ({
          name: label,
          deviceId,
        })),
        selectedDeviceId: detectedVideoDevices[0]?.deviceId || '',
      });
    }
  }

  private switchCamera() {
    const { selectedDeviceId } = this.state;
    this.setState({ selectedDeviceId: selectedDeviceId === 'user' ? 'environment' : 'user' });
  }

  private onDataAvailable({ data }: BlobEvent) {
    const { finishedRecording } = this.state;
    if (data.size > 0 && finishedRecording) {
      this.setState((prevState) => ({
        videoRecorded: [data],
        capturedImage: null,
        finishedRecording: false,
      }));
      message.success('Capture successful');
    }
  }

  private closeWebcam() {
    this.setState(
      {
        isCapturing: false,
        isRecording: false,
        capturedImage: null,
        videoRecorded: [],
        webcamOpened: false,
        finishedRecording: false,
      },
      () => {
        this.mediaRecorderRef?.stop();
      }
    );
  }

  private openCapturePhoto() {
    this.setState({
      isCapturing: true,
      isRecording: false,
      capturedImage: null,
      videoRecorded: [],
      webcamOpened: true,
    });
  }

  private openCaptureVideo() {
    this.setState({
      isCapturing: false,
      isRecording: false,
      capturedImage: null,
      videoRecorded: [],
      webcamOpened: true,
    });
  }

  private capturePhoto() {
    const imageSrc = this.webcamRef.current?.getScreenshot();
    if (imageSrc) {
      message.success('Capture successful');
      this.setState({
        capturedImage: imageSrc,
        videoRecorded: [],
        webcamOpened: false,
      });
    } else {
      message.error('Capture failed');
    }
    this.setState({ isCapturing: false });
  }

  private stopVideo() {
    if (!this.mediaRecorderRef) {
      return;
    }
    this.setState({ isRecording: false, webcamOpened: false, finishedRecording: true }, () => {
      this.mediaRecorderRef?.stop();
    });
  }

  private getSupportedMimeTypes() {
    const mimeTypes = [
      'video/webm; codecs=vp9',
      'video/webm; codecs=vp9,opus',
      'video/webm; codecs=vp8,opus',
      'video/webm; codecs=vp8',
      'video/webm',
      'video/mp4; codecs=h264,aac',
      'video/mp4; codecs=h264',
      'video/mp4',
      'video/mov; codecs=h264,aac',
      'video/mov; codecs=h264',
      'video/mov',
    ];
    const supportedMimeTypes = mimeTypes.filter((mimeType) => MediaRecorder.isTypeSupported(mimeType));
    if (supportedMimeTypes.length === 0) {
      message.error('No supported mime types found');
      return null;
    }
    return supportedMimeTypes[0];
  }

  private captureVideo() {
    this.setState({ isRecording: true, capturedImage: null, videoRecorded: [] }, () => {
      setTimeout(() => {
        if (!this.webcamRef.current || !this.webcamRef.current.stream) {
          return;
        }
        const mimeType = this.getSupportedMimeTypes();
        if (!mimeType) {
          this.setState({ isRecording: false, capturedImage: null, videoRecorded: [] });
          return;
        }
        this.mediaRecorderRef = new MediaRecorder(this.webcamRef.current.stream, {
          mimeType,
        });
        this.mediaRecorderRef.addEventListener('dataavailable', this.onDataAvailable);
        this.mediaRecorderRef.start();
      }, 500);
    });
  }

  private renderVideoButtons() {
    const { isCapturing, isRecording } = this.state;
    if (isCapturing) {
      return null;
    }

    if (isRecording) {
      return (
        <Button type="primary" onClick={this.stopVideo}>
          Stop video
        </Button>
      );
    }
    return (
      <Button type="primary" onClick={this.captureVideo}>
        {this.state.videoRecorded.length === 0 ? 'Start recording' : 'Record again'}
      </Button>
    );
  }
  private renderPhotoButtons() {
    const { isCapturing, isRecording } = this.state;
    if (isRecording || !isCapturing) {
      return null;
    }
    return (
      <Button type="primary" onClick={this.capturePhoto}>
        Capture
      </Button>
    );
  }

  private renderPreview() {
    const { videoRecorded, capturedImage, isRecording } = this.state;
    if ((!videoRecorded.length && !capturedImage) || isRecording) {
      return null;
    }
    if (capturedImage) {
      return (
        <div>
          <h2>Preview</h2>
          <img className={styles.photoPreview} src={capturedImage} alt="Captured" />
        </div>
      );
    }
    const videoBlob = new Blob(videoRecorded, { type: this.getSupportedMimeTypes() ?? 'video/webm' });
    const videoUrl = URL.createObjectURL(videoBlob);
    return (
      <div>
        <h2>Preview</h2>
        <video className={styles.videoPreview} controls src={videoUrl} />
      </div>
    );
  }

  private renderCameraSelector() {
    const { devices, selectedDeviceId } = this.state;
    if (devices.length === 0) {
      return <Button onClick={this.switchCamera}>Switch camera</Button>;
    }
    return (
      <select
        className={styles.cameraSelector}
        value={selectedDeviceId}
        onChange={(e) => {
          this.setState({ selectedDeviceId: e.target.value });
        }}
      >
        {devices.map(({ name, deviceId }) => (
          <option key={deviceId} value={deviceId}>
            {name}
          </option>
        ))}
      </select>
    );
  }

  private renderWebcam() {
    const { webcamOpened, selectedDeviceId } = this.state;
    if (!webcamOpened) {
      return null;
    }
    let videoConstraints: MediaTrackConstraints = {
      deviceId: selectedDeviceId,
    };
    if (isMobileOrTablet()) {
      if (selectedDeviceId === 'user') {
        videoConstraints = {
          facingMode: 'user',
        };
      } else {
        videoConstraints = {
          facingMode: { exact: 'environment' },
        };
      }
    }
    return (
      <>
        <Webcam
          className={styles.webcam}
          audio={false}
          ref={this.webcamRef}
          screenshotFormat="image/jpeg"
          download
          videoConstraints={videoConstraints}
        />
        {this.renderCameraSelector()}
      </>
    );
  }

  private renderUploadFile() {
    return (
      <>
        <label htmlFor="fileInput" className={styles.uploadButton}>
          Select file
        </label>
        <input
          id="fileInput"
          type="file"
          accept="video/*|image/*"
          className={styles.uploadButtonHide}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) {
              return;
            }

            if (['image/gif', 'image/jpeg', 'image/png'].includes(file.type)) {
              //is an image
              this.setState({
                capturedImage: URL.createObjectURL(file),
                videoRecorded: [],
              });
              return;
            }
            if (['video/webm', 'video/mp4', 'video/mov', 'video/quicktime'].includes(file.type)) {
              // is a video
              const videoBlob = new Blob([file], { type: file.type });
              this.setState({
                videoRecorded: [videoBlob],
                capturedImage: null,
              });
              return;
            }
            message.error('File type not supported');
          }}
        />
      </>
    );
  }

  private renderCancelButton() {
    return (
      <Button onClick={this.closeWebcam} type="default">
        Cancel
      </Button>
    );
  }

  private renderMainButtons() {
    const { webcamOpened } = this.state;
    if (!webcamOpened) {
      return (
        <>
          {this.renderUploadFile()}
          <Button onClick={this.openCapturePhoto} type="primary">
            Photo
          </Button>
          <Button onClick={this.openCaptureVideo} type="primary">
            Video
          </Button>
        </>
      );
    }
    return (
      <>
        {this.renderCancelButton()}
        {this.renderPhotoButtons()}
        {this.renderVideoButtons()}
      </>
    );
  }

  render() {
    return (
      <div className={styles.container}>
        {this.renderWebcam()}
        {this.renderPreview()}
        <div className={styles.buttons}>{this.renderMainButtons()}</div>
      </div>
    );
  }
}
